'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '@/lib/hooks/useChat';
import toast, { Toaster } from 'react-hot-toast';
import { Home, Flame, BarChart2, Bookmark, FileText, Settings, Search, Zap, X, Copy, ChevronRight, Play, Eye, Heart, MessageCircle, TrendingUp, Plus, Menu } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface VideoCard {
  id: number;
  title: string;
  creator: string;
  platform: 'tiktok' | 'instagram' | 'youtube' | 'facebook';
  niche: string;
  views: string;
  likes: string;
  comments: string;
  outlierScore: string;
  thumbnailGradient: string;
  youtubeId?: string;
  tiktokVideoId?: string;
  tiktokUser?: string;
  instagramPermalink?: string;
  formats: string[];
}

interface GeneratedIdea {
  title: string;
  description: string;
  platform: string;
  format: string;
}

interface WatchlistEntry {
  icon: string;
  name: string;
  count: number;
  platforms: string[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const PLATFORM_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  tiktok:    { label: 'TikTok',           icon: '🎵', color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
  instagram: { label: 'Instagram Reels',  icon: '📸', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  youtube:   { label: 'YouTube Shorts',   icon: '▶️', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  facebook:  { label: 'Facebook Reels',   icon: '👥', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
};

const NICHE_COLORS: Record<string, string> = {
  Finance:    'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  Fitness:    'bg-orange-500/20 text-orange-300 border-orange-500/30',
  Business:   'bg-violet-500/20 text-violet-300 border-violet-500/30',
  Tech:       'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  Food:       'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  Travel:     'bg-sky-500/20 text-sky-300 border-sky-500/30',
  Comedy:     'bg-amber-500/20 text-amber-300 border-amber-500/30',
  Motivation: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  Beauty:     'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
  Gaming:     'bg-lime-500/20 text-lime-300 border-lime-500/30',
};

// Platform accent line gradients
const PLATFORM_ACCENT: Record<string, string> = {
  tiktok:    'linear-gradient(90deg, #ff0050, #ff4080)',
  instagram: 'linear-gradient(90deg, #833ab4, #fd1d1d, #fcb045)',
  youtube:   'linear-gradient(90deg, #ff0000, #ff4444)',
  facebook:  'linear-gradient(90deg, #1877f2, #42a5f5)',
};

const VIDEO_CARDS: VideoCard[] = [
  {
    id: 1,
    title: 'How I Made $10,000 in 30 Days',
    creator: '@grahamstephan',
    platform: 'tiktok',
    niche: 'Finance',
    views: '4.2M',
    likes: '380K',
    comments: '12K',
    outlierScore: '18.2x',
    thumbnailGradient: 'from-emerald-900 via-emerald-800 to-teal-900',
    tiktokVideoId: '7243100059874238763',
    tiktokUser: 'grahamstephan',
    formats: ['Hook Ideas', 'Trend Angles'],
  },
  {
    id: 2,
    title: 'I Tried Waking Up at 4AM for 30 Days',
    creator: '@davidgoggins',
    platform: 'instagram',
    niche: 'Fitness',
    views: '8.7M',
    likes: '620K',
    comments: '24K',
    outlierScore: '22.1x',
    thumbnailGradient: 'from-orange-900 via-red-900 to-rose-900',
    instagramPermalink: 'https://www.instagram.com/reel/C8k9X2rNqAB/',
    formats: ['Storytime', 'Challenges'],
  },
  {
    id: 3,
    title: '$0 to $1M Business Model Nobody Talks About',
    creator: '@alexhormozi',
    platform: 'youtube',
    niche: 'Business',
    views: '11.2M',
    likes: '890K',
    comments: '31K',
    outlierScore: '31.4x',
    thumbnailGradient: 'from-violet-900 via-purple-900 to-indigo-900',
    youtubeId: 'Qg4DQITpRSI',
    formats: ['Hook Ideas', 'Tutorial'],
  },
  {
    id: 4,
    title: 'This AI Tool Replaced My Entire Workflow',
    creator: '@heyitsalexia',
    platform: 'tiktok',
    niche: 'Tech',
    views: '6.3M',
    likes: '510K',
    comments: '18K',
    outlierScore: '15.7x',
    thumbnailGradient: 'from-cyan-900 via-blue-900 to-indigo-900',
    tiktokVideoId: '7319826534217988394',
    tiktokUser: 'heyitsalexia',
    formats: ['Hook Ideas', 'Tutorial'],
  },
  {
    id: 5,
    title: 'The Viral Dubai Chocolate Bar Recipe',
    creator: '@feelgoodfoodie',
    platform: 'instagram',
    niche: 'Food',
    views: '22.4M',
    likes: '1.8M',
    comments: '62K',
    outlierScore: '44.2x',
    thumbnailGradient: 'from-amber-900 via-yellow-900 to-orange-900',
    instagramPermalink: 'https://www.instagram.com/reel/C5tK3LVuqmN/',
    formats: ['Tutorial', 'Trend Angles'],
  },
  {
    id: 6,
    title: '$1 vs $1,000,000 Hotel Room',
    creator: '@mrbeast',
    platform: 'youtube',
    niche: 'Travel',
    views: '47.1M',
    likes: '3.2M',
    comments: '98K',
    outlierScore: '88.6x',
    thumbnailGradient: 'from-sky-900 via-blue-900 to-cyan-900',
    youtubeId: 'WXuK6gekU1Y',
    formats: ['Challenges', 'Storytime'],
  },
  {
    id: 7,
    title: "When You're the Only One Who Didn't Get the Memo",
    creator: '@khaby.lame',
    platform: 'tiktok',
    niche: 'Comedy',
    views: '34.8M',
    likes: '4.1M',
    comments: '54K',
    outlierScore: '67.3x',
    thumbnailGradient: 'from-amber-900 via-orange-900 to-red-900',
    tiktokVideoId: '7016570981296438533',
    tiktokUser: 'khaby.lame',
    formats: ['POV', 'Hook Ideas'],
  },
  {
    id: 8,
    title: 'Nobody Is Coming To Save You',
    creator: '@garyvee',
    platform: 'instagram',
    niche: 'Motivation',
    views: '9.1M',
    likes: '730K',
    comments: '28K',
    outlierScore: '19.8x',
    thumbnailGradient: 'from-rose-900 via-pink-900 to-fuchsia-900',
    instagramPermalink: 'https://www.instagram.com/reel/CqY8XvMu3Rd/',
    formats: ['Hook Ideas', 'Storytime'],
  },
  {
    id: 9,
    title: "I Did the 'That Girl' Routine for 21 Days",
    creator: '@saggysara',
    platform: 'tiktok',
    niche: 'Beauty',
    views: '13.6M',
    likes: '1.1M',
    comments: '41K',
    outlierScore: '27.5x',
    thumbnailGradient: 'from-fuchsia-900 via-pink-900 to-rose-900',
    tiktokVideoId: '7187692948949294338',
    tiktokUser: 'saggysara',
    formats: ['Challenges', 'Storytime'],
  },
  {
    id: 10,
    title: '3 Money Habits That Changed My Life at 25',
    creator: '@humphreytalks',
    platform: 'tiktok',
    niche: 'Finance',
    views: '7.8M',
    likes: '640K',
    comments: '22K',
    outlierScore: '24.3x',
    thumbnailGradient: 'from-emerald-900 via-teal-900 to-cyan-900',
    tiktokVideoId: '7021870680052765957',
    tiktokUser: 'humphreytalks',
    formats: ['Hook Ideas', 'Trend Angles'],
  },
  {
    id: 11,
    title: 'The One Skill That Makes You Unemployable',
    creator: '@alexhormozi',
    platform: 'instagram',
    niche: 'Business',
    views: '18.3M',
    likes: '1.4M',
    comments: '47K',
    outlierScore: '38.9x',
    thumbnailGradient: 'from-violet-900 via-indigo-900 to-purple-900',
    instagramPermalink: 'https://www.instagram.com/reel/CuWpqEWu9ZN/',
    formats: ['Hook Ideas', 'Tutorial'],
  },
  {
    id: 12,
    title: 'I Played the Hardest Game for 24 Hours Straight',
    creator: '@ishowspeed',
    platform: 'youtube',
    niche: 'Gaming',
    views: '29.5M',
    likes: '2.6M',
    comments: '83K',
    outlierScore: '55.1x',
    thumbnailGradient: 'from-lime-900 via-green-900 to-emerald-900',
    youtubeId: 'n9GCkE9wMLE',
    formats: ['Challenges', 'Storytime'],
  },
];

const NICHES_FILTER = [
  'Fitness & Health', 'Food & Cooking', 'Travel', 'Finance & Money',
  'Beauty & Fashion', 'Tech & Gadgets', 'Comedy', 'Education',
  'Business', 'Lifestyle', 'Gaming', 'Pets', 'DIY & Crafts', 'Motivation',
];

const FORMAT_FILTERS = [
  'Hook Ideas', 'Trend Angles', 'Series Concepts', 'Challenges', 'Storytime', 'Tutorial', 'POV',
];

const TRENDING_TAGS = [
  '#AITools', '#MoneyTok', '#GymTok', '#DubaiChocolate', '#SilentWalking',
  '#QuietLuxury', '#FinanceTok', '#StudyWithMe', '#VanLife', '#MorningRoutine',
  '#DigitalNomad', '#CryptoTok', '#CleanEating', '#SideHustle', '#TravelTok',
  '#AITools', '#MoneyTok', '#GymTok', '#DubaiChocolate', '#SilentWalking',
];

const WATCHLISTS: WatchlistEntry[] = [
  { icon: '💼', name: 'Business Creators', count: 18, platforms: ['🎵', '▶️', '📸', '👥'] },
  { icon: '🎯', name: 'Finance Outliers',  count: 12, platforms: ['🎵', '▶️', '📸'] },
  { icon: '💪', name: 'Fitness & Health',  count: 9,  platforms: ['📸', '🎵', '▶️'] },
];

const GENERATOR_NICHES = [
  'Fitness & Health', 'Food & Cooking', 'Travel', 'Finance & Money',
  'Beauty & Fashion', 'Tech & Gadgets', 'Comedy', 'Education',
  'Business', 'Lifestyle', 'Gaming', 'Pets',
];

const GENERATOR_FORMATS = ['Hook', 'Trend Angle', 'Series', 'Challenge'];

// ─── Video Embed Components ───────────────────────────────────────────────────

function YouTubeEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl"
      style={{ aspectRatio: '9/16', maxHeight: '480px', background: '#1a1a1a' }}
    >
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '12px' }}
      />
    </div>
  );
}

function TikTokEmbed({ videoId, user }: { videoId: string; user: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Re-process TikTok embeds after mount
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).tiktokEmbed) {
        (window as any).tiktokEmbed.render();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [videoId]);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-xl"
      style={{ aspectRatio: '9/16', maxHeight: '480px', background: '#1a1a1a' }}
    >
      <iframe
        src={`https://www.tiktok.com/embed/v2/${videoId}`}
        title={`TikTok by @${user}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '12px' }}
      />
    </div>
  );
}

function InstagramEmbed({ permalink }: { permalink: string }) {
  useEffect(() => {
    // Process Instagram embeds after mount
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).instgrm) {
        (window as any).instgrm.Embeds.process();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [permalink]);

  // Extract shortcode from permalink for iframe embed
  const shortcode = permalink.replace(/\/$/, '').split('/').pop() || '';

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl"
      style={{ aspectRatio: '9/16', maxHeight: '480px', background: '#1a1a1a' }}
    >
      <iframe
        src={`https://www.instagram.com/reel/${shortcode}/embed/`}
        title="Instagram Reel"
        frameBorder="0"
        scrolling="no"
        allowTransparency
        allowFullScreen
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '12px' }}
      />
    </div>
  );
}

function VideoEmbed({ card }: { card: VideoCard }) {
  if (card.platform === 'youtube' && card.youtubeId) {
    return <YouTubeEmbed videoId={card.youtubeId} title={card.title} />;
  }
  if (card.platform === 'tiktok' && card.tiktokVideoId && card.tiktokUser) {
    return <TikTokEmbed videoId={card.tiktokVideoId} user={card.tiktokUser} />;
  }
  if (card.platform === 'instagram' && card.instagramPermalink) {
    return <InstagramEmbed permalink={card.instagramPermalink} />;
  }
  // Fallback gradient placeholder
  return (
    <div
      className={`w-full bg-gradient-to-br ${card.thumbnailGradient} flex items-center justify-center relative overflow-hidden rounded-xl`}
      style={{ aspectRatio: '9/16', maxHeight: '480px' }}
    >
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.1) 0%, transparent 60%)' }} />
      <Play className="w-10 h-10 text-white/30" />
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.02] animate-pulse" style={{ maxWidth: '340px' }}>
      <div className="w-full bg-white/[0.05] rounded-xl" style={{ aspectRatio: '9/16', maxHeight: '480px' }} />
      <div className="p-4 space-y-3">
        <div className="h-3 bg-white/[0.06] rounded w-3/4" />
        <div className="h-3 bg-white/[0.06] rounded w-1/2" />
        <div className="flex gap-2 mt-4">
          <div className="h-6 bg-white/[0.06] rounded w-16" />
          <div className="h-6 bg-white/[0.06] rounded w-16" />
          <div className="h-6 bg-white/[0.06] rounded w-16" />
        </div>
      </div>
    </div>
  );
}

// ─── Analyze Drawer ───────────────────────────────────────────────────────────

function AnalyzeDrawer({ card, onClose }: { card: VideoCard; onClose: () => void }) {
  const pl = PLATFORM_LABELS[card.platform];
  const nicheColor = NICHE_COLORS[card.niche] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed right-0 top-0 h-full w-full max-w-md z-50 bg-[#0d0d0d] border-l border-white/[0.08] overflow-y-auto"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">Video Analysis</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06] text-neutral-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <VideoEmbed card={card} />

        <div className="mt-4 space-y-1">
          <div className="flex gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${pl.color}`}>{pl.icon} {pl.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${nicheColor}`}>{card.niche}</span>
          </div>
          <h3 className="text-white font-semibold text-base mt-2">{card.title}</h3>
          <p className="text-neutral-500 text-sm">{card.creator}</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[['👁', card.views, 'Views'], ['❤️', card.likes, 'Likes'], ['💬', card.comments, 'Comments']].map(([icon, val, label]) => (
            <div key={label as string} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
              <div className="text-lg">{icon}</div>
              <div className="text-white font-bold text-sm">{val}</div>
              <div className="text-neutral-500 text-xs">{label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
            <h4 className="text-violet-400 font-semibold text-sm mb-2">🎣 Hook Breakdown</h4>
            <p className="text-neutral-300 text-sm leading-relaxed">
              Opens with a bold claim that triggers curiosity and FOMO. The specific numbers and timeframe create a concrete, believable promise that makes viewers want to know the method.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
            <h4 className="text-violet-400 font-semibold text-sm mb-3">📖 Storytelling Structure</h4>
            <div className="space-y-2">
              {[
                ['Hook', 'Bold claim + specific result in first 3 seconds'],
                ['Build', 'Step-by-step breakdown with proof points'],
                ['Payoff', 'Actionable takeaway + CTA to follow'],
              ].map(([stage, desc]) => (
                <div key={stage} className="flex gap-3">
                  <span className="text-xs font-bold text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded h-fit mt-0.5 shrink-0">{stage}</span>
                  <span className="text-neutral-400 text-sm">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
            <h4 className="text-amber-400 font-semibold text-sm mb-2">🔥 Why This Went Viral ({card.outlierScore})</h4>
            <p className="text-neutral-300 text-sm leading-relaxed">
              Combines aspirational claim with relatable premise — removes the biggest objection. High outlier score indicates massive over-performance vs channel average.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
            <h4 className="text-violet-400 font-semibold text-sm mb-3">💡 Similar Ideas to Try</h4>
            <ul className="space-y-2">
              {[
                'How I Made $5K in 2 Weeks Flipping Items on eBay',
                "I Tried 5 Side Hustles — Here's What Actually Worked",
                'The $0 Business Model That Made Me Quit My Job',
              ].map((idea) => (
                <li key={idea} className="flex items-start gap-2 text-sm text-neutral-400">
                  <span className="text-violet-400 mt-0.5">→</span>
                  {idea}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IdeasPage() {
  const [activePlatform, setActivePlatform] = useState('all');
  const [activeNiches, setActiveNiches] = useState<string[]>([]);
  const [activeFormats, setActiveFormats] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [analyzeCard, setAnalyzeCard] = useState<VideoCard | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Generator state
  const [genPlatform, setGenPlatform] = useState('TikTok');
  const [genNiche, setGenNiche] = useState('');
  const [genFormat, setGenFormat] = useState('Hook');
  const [genAudience, setGenAudience] = useState('');
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const tickerRef = useRef<HTMLDivElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-sonnet-4-6', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const jsonMatch = response.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          setGeneratedIdeas(parsed.slice(0, 6));
          setHasGenerated(true);
        }
      } catch {
        const lines = response.split('\n').filter(l => l.trim().length > 10).slice(0, 6);
        setGeneratedIdeas(lines.map((l) => ({
          title: l.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').trim(),
          description: 'AI-generated viral video concept tailored to your niche.',
          platform: genPlatform,
          format: genFormat,
        })));
        setHasGenerated(true);
      }
    }
  }, [response, isLoading, genPlatform, genFormat]);

  const handleGenerate = useCallback(() => {
    if (!genNiche) { toast.error('Select a niche first'); return; }

    const systemPrompt = `You are a viral content strategist. Given a platform, niche, content format, and target audience, generate exactly 6 specific, scroll-stopping viral video ideas. For each idea, provide: a punchy title, a 1-sentence description, and the content format. Return as JSON array with objects: { title, description, platform, format }`;

    const userPrompt = `Platform: ${genPlatform}
Niche: ${genNiche}
Format: ${genFormat}
${genAudience ? `Target Audience: ${genAudience}` : ''}

Generate 6 viral video ideas as a JSON array.`;

    sendMessage(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      { max_tokens: 1200 }
    );
  }, [genPlatform, genNiche, genFormat, genAudience, sendMessage]);

  const copyHook = (card: VideoCard) => {
    const hook = `${card.title} — ${card.creator}`;
    navigator.clipboard.writeText(hook).then(() => toast.success('Hook copied!'));
  };

  const copyIdea = (idea: GeneratedIdea) => {
    navigator.clipboard.writeText(`${idea.title}\n\n${idea.description}`).then(() => toast.success('Copied!'));
  };

  const toggleNiche = (n: string) => setActiveNiches(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
  const toggleFormat = (f: string) => setActiveFormats(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const filteredCards = VIDEO_CARDS.filter(card => {
    if (activePlatform !== 'all' && card.platform !== activePlatform) return false;
    if (activeNiches.length > 0 && !activeNiches.some(n => n.toLowerCase().includes(card.niche.toLowerCase()) || card.niche.toLowerCase().includes(n.toLowerCase()))) return false;
    if (activeFormats.length > 0 && !activeFormats.some(f => card.formats.includes(f))) return false;
    if (searchQuery && !card.title.toLowerCase().includes(searchQuery.toLowerCase()) && !card.creator.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const navItems = [
    { icon: <Home className="w-4 h-4" />, label: 'Home', href: '/' },
    { icon: <Flame className="w-4 h-4" />, label: 'Viral Ideas', href: '/ideas', active: true },
    { icon: <BarChart2 className="w-4 h-4" />, label: 'Trend Explorer', href: '#' },
    { icon: <Bookmark className="w-4 h-4" />, label: 'Watchlists', href: '#' },
    { icon: <FileText className="w-4 h-4" />, label: 'Script Writer', href: '#' },
    { icon: <Settings className="w-4 h-4" />, label: 'Settings', href: '#' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex overflow-x-hidden" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' } }} />

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── LEFT SIDEBAR ── */}
      <aside className={`fixed left-0 top-0 h-full w-[260px] bg-[#0d0d0d] border-r border-white/[0.06] flex flex-col z-40 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className="p-6 border-b border-white/[0.06]">
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-600/30">
              <Zap className="w-4 h-4 text-white fill-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Scalio</span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                item.active
                  ? 'bg-violet-600/20 text-violet-300 border border-violet-500/30'
                  : 'text-neutral-500 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              <span className={item.active ? 'text-violet-400' : 'text-neutral-600 group-hover:text-neutral-300 transition-colors'}>{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        {/* AI Status */}
        <div className="px-4 py-3 mx-4 mb-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-emerald-400 text-xs font-semibold">AI Agent Active</span>
        </div>

        {/* User + Upgrade */}
        <div className="p-4 border-t border-white/[0.06] space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center text-xs font-bold text-white">S</div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">Scalio User</p>
              <a href="https://scalio.vip" target="_blank" rel="noopener noreferrer" className="text-neutral-500 text-xs hover:text-violet-400 transition-colors">scalio.vip ↗</a>
            </div>
          </div>
          <button className="w-full py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all duration-200 shadow-lg shadow-violet-600/20 hover:shadow-violet-500/30">
            Upgrade to Pro ✨
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 lg:ml-[260px] min-h-screen flex flex-col">

        {/* ── TOP HEADER ── */}
        <header className="sticky top-0 z-20 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/[0.06] px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/[0.06] text-neutral-400 hover:text-white transition-colors">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">Viral Ideas Hub</h1>
                <p className="text-neutral-500 text-sm hidden sm:block">Discover what&apos;s going viral. Generate ideas tailored to your niche.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2">
                <Search className="w-4 h-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search niches, topics, creators..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="bg-transparent text-sm text-white placeholder-neutral-600 outline-none w-52"
                />
              </div>
              <button
                onClick={handleGenerate}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-semibold transition-all duration-200 shadow-lg shadow-violet-600/20 hover:shadow-violet-500/30 whitespace-nowrap"
              >
                <Zap className="w-4 h-4 fill-white" />
                <span className="hidden sm:inline">Generate Ideas</span>
              </button>
            </div>
          </div>
        </header>

        <div className="flex-1 flex">
          {/* ── CENTER FEED ── */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">

              {/* ── TRENDING TICKER ── */}
              <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] py-3 relative">
                <div className="flex items-center gap-3 px-4 mb-2">
                  <TrendingUp className="w-4 h-4 text-violet-400 shrink-0" />
                  <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider">Trending Now</span>
                </div>
                <div
                  ref={tickerRef}
                  className="flex gap-2 px-4 overflow-x-auto scrollbar-hide"
                  style={{ scrollbarWidth: 'none' }}
                >
                  <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}`}</style>
                  <div className="flex gap-2 animate-[scroll_30s_linear_infinite] hover:[animation-play-state:paused] min-w-max">
                    {TRENDING_TAGS.map((tag, i) => (
                      <button
                        key={`${tag}-${i}`}
                        onClick={() => setSearchQuery(tag.replace('#', ''))}
                        className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-neutral-400 hover:text-violet-300 hover:border-violet-500/40 hover:bg-violet-500/10 text-xs font-medium transition-all duration-200 whitespace-nowrap"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── PLATFORM FILTERS ── */}
              <div className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  {[{ id: 'all', label: 'All Platforms', icon: '🌐' }, { id: 'tiktok', label: 'TikTok', icon: '🎵' }, { id: 'instagram', label: 'Instagram Reels', icon: '📸' }, { id: 'youtube', label: 'YouTube Shorts', icon: '▶️' }, { id: 'facebook', label: 'Facebook Reels', icon: '👥' }].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setActivePlatform(p.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${activePlatform === p.id ? 'bg-violet-600 border-violet-500 text-white shadow-lg shadow-violet-600/20' : 'bg-transparent border-white/[0.1] text-neutral-400 hover:border-violet-500/40 hover:text-white'}`}
                    >
                      <span>{p.icon}</span> {p.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 flex-wrap">
                  {NICHES_FILTER.map(n => (
                    <button
                      key={n}
                      onClick={() => toggleNiche(n)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${activeNiches.includes(n) ? 'bg-violet-600/30 border-violet-500/60 text-violet-200' : 'bg-transparent border-white/[0.08] text-neutral-500 hover:border-white/20 hover:text-neutral-300'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 flex-wrap">
                  {FORMAT_FILTERS.map(f => (
                    <button
                      key={f}
                      onClick={() => toggleFormat(f)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${activeFormats.includes(f) ? 'bg-violet-600/30 border-violet-500/60 text-violet-200' : 'bg-transparent border-white/[0.08] text-neutral-500 hover:border-white/20 hover:text-neutral-300'}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── VIDEO GRID ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 justify-items-center">
                <AnimatePresence mode="popLayout">
                  {filteredCards.map((card, i) => {
                    const pl = PLATFORM_LABELS[card.platform];
                    const nicheColor = NICHE_COLORS[card.niche] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
                    const accentGradient = PLATFORM_ACCENT[card.platform];
                    return (
                      <motion.div
                        key={card.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                        className="rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.02] hover:border-violet-500/30 hover:bg-white/[0.04] transition-all duration-300 group flex flex-col w-full"
                        style={{ maxWidth: '340px' }}
                      >
                        {/* Video embed with platform badge overlay */}
                        <div className="relative">
                          <VideoEmbed card={card} />
                          {/* Platform badge top-left */}
                          <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full border backdrop-blur-sm z-10 ${pl.color}`}>
                            {pl.icon} {pl.label}
                          </span>
                          {/* Niche badge top-right */}
                          <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full border backdrop-blur-sm z-10 ${nicheColor}`}>
                            {card.niche}
                          </span>
                        </div>

                        {/* Platform accent line */}
                        <div className="h-[3px] w-full" style={{ background: accentGradient }} />

                        <div className="p-4 flex flex-col flex-1">
                          <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2 mb-1">{card.title}</h3>
                          <p className="text-neutral-500 text-xs mb-3">{card.creator}</p>

                          <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{card.views}</span>
                            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{card.likes}</span>
                            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{card.comments}</span>
                          </div>

                          <div className="flex items-center justify-between mb-4">
                            <span className="text-xs font-bold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">🔥 {card.outlierScore}</span>
                          </div>

                          <div className="flex gap-2 mt-auto">
                            <button
                              onClick={() => setAnalyzeCard(card)}
                              className="flex-1 py-1.5 rounded-lg border border-white/[0.1] text-neutral-400 hover:text-white hover:border-violet-500/40 hover:bg-violet-500/10 text-xs font-medium transition-all duration-200"
                            >
                              Analyze
                            </button>
                            <button
                              onClick={() => copyHook(card)}
                              className="flex-1 py-1.5 rounded-lg border border-white/[0.1] text-neutral-400 hover:text-white hover:border-violet-500/40 hover:bg-violet-500/10 text-xs font-medium transition-all duration-200 flex items-center justify-center gap-1"
                            >
                              <Copy className="w-3 h-3" /> Hook
                            </button>
                            <button
                              onClick={() => toast('Script Writer coming soon!')}
                              className="flex-1 py-1.5 rounded-lg border border-white/[0.1] text-neutral-400 hover:text-white hover:border-violet-500/40 hover:bg-violet-500/10 text-xs font-medium transition-all duration-200"
                            >
                              Script
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {/* Skeleton loaders */}
                {[1, 2, 3].map(i => <SkeletonCard key={`sk-${i}`} />)}
              </div>

              {filteredCards.length === 0 && (
                <div className="text-center py-16 text-neutral-600">
                  <p className="text-4xl mb-3">🎬</p>
                  <p className="font-semibold text-neutral-400">No videos match your filters</p>
                  <p className="text-sm mt-1">Try adjusting your platform or niche selection</p>
                </div>
              )}

              {/* ── SCRIPT WRITER TEASER ── */}
              <div className="rounded-2xl overflow-hidden border border-violet-500/20 bg-gradient-to-r from-violet-900/40 via-purple-900/30 to-indigo-900/40 p-8 flex flex-col sm:flex-row items-center gap-6">
                <div className="text-5xl">✍️</div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-white font-bold text-xl mb-1">Turn Any Viral Video Into Your Own Script</h3>
                  <p className="text-neutral-400 text-sm">Paste a video URL or pick from your feed — Scalio AI writes a custom script in your voice, in seconds.</p>
                </div>
                <button className="px-6 py-3 rounded-xl bg-white text-gray-900 font-bold text-sm hover:bg-neutral-100 transition-colors whitespace-nowrap shadow-lg">
                  Open Script Writer →
                </button>
              </div>

            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <aside className="hidden xl:flex flex-col w-[320px] shrink-0 border-l border-white/[0.06] bg-[#0d0d0d] overflow-y-auto">
            <div className="p-5 space-y-6">

              {/* ── AI GENERATOR ── */}
              <div>
                <h2 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                  <span className="text-lg">🧠</span> Generate Viral Ideas
                </h2>

                <div className="space-y-4">
                  {/* Platform */}
                  <div>
                    <label className="text-xs text-neutral-500 font-medium mb-2 block uppercase tracking-wider">Platform</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {['TikTok', 'Instagram', 'YouTube', 'Facebook'].map(p => (
                        <button
                          key={p}
                          onClick={() => setGenPlatform(p)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-200 ${genPlatform === p ? 'bg-violet-600 border-violet-500 text-white' : 'border-white/[0.1] text-neutral-500 hover:text-white hover:border-white/20'}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Niche */}
                  <div>
                    <label className="text-xs text-neutral-500 font-medium mb-2 block uppercase tracking-wider">Niche</label>
                    <select
                      value={genNiche}
                      onChange={e => setGenNiche(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-violet-500/50 transition-colors appearance-none"
                    >
                      <option value="" className="bg-[#1a1a1a]">Select a niche...</option>
                      {GENERATOR_NICHES.map(n => <option key={n} value={n} className="bg-[#1a1a1a]">{n}</option>)}
                    </select>
                  </div>

                  {/* Format */}
                  <div>
                    <label className="text-xs text-neutral-500 font-medium mb-2 block uppercase tracking-wider">Format</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {GENERATOR_FORMATS.map(f => (
                        <button
                          key={f}
                          onClick={() => setGenFormat(f)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-200 ${genFormat === f ? 'bg-violet-600 border-violet-500 text-white' : 'border-white/[0.1] text-neutral-500 hover:text-white hover:border-white/20'}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Audience */}
                  <div>
                    <label className="text-xs text-neutral-500 font-medium mb-2 block uppercase tracking-wider">Target Audience</label>
                    <input
                      type="text"
                      value={genAudience}
                      onChange={e => setGenAudience(e.target.value)}
                      placeholder="e.g. beginner investors, moms, Gen Z..."
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white placeholder-neutral-600 outline-none focus:border-violet-500/50 transition-colors"
                    />
                  </div>

                  <button
                    onClick={handleGenerate}
                    disabled={isLoading}
                    className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-violet-600/20 hover:shadow-violet-500/30 flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <><Zap className="w-4 h-4 fill-white" /> ⚡ Generate Ideas</>
                    )}
                  </button>
                </div>

                {/* Output */}
                <div className="mt-4">
                  {!hasGenerated && !isLoading && (
                    <div className="text-center py-8 text-neutral-600">
                      <p className="text-3xl mb-2">🎬</p>
                      <p className="text-xs leading-relaxed">Configure your settings and hit Generate — 6 viral ideas in seconds.</p>
                    </div>
                  )}

                  {isLoading && (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 animate-pulse">
                          <div className="h-3 bg-white/[0.06] rounded w-3/4 mb-2" />
                          <div className="h-2 bg-white/[0.04] rounded w-full" />
                        </div>
                      ))}
                    </div>
                  )}

                  {hasGenerated && !isLoading && (
                    <div className="space-y-2">
                      {generatedIdeas.map((idea, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.08 }}
                          className="rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-violet-500/30 p-3 transition-all duration-200"
                        >
                          <p className="text-white font-semibold text-xs leading-snug mb-1">{idea.title}</p>
                          <p className="text-neutral-500 text-xs line-clamp-2 mb-2">{idea.description}</p>
                          <div className="flex gap-1.5">
                            <button onClick={() => copyIdea(idea)} className="px-2 py-0.5 rounded-md border border-white/[0.08] text-neutral-500 hover:text-white hover:border-violet-500/40 text-xs transition-all duration-200 flex items-center gap-1">
                              <Copy className="w-2.5 h-2.5" /> Copy
                            </button>
                            <button className="px-2 py-0.5 rounded-md border border-white/[0.08] text-neutral-500 hover:text-white hover:border-violet-500/40 text-xs transition-all duration-200">
                              Expand
                            </button>
                            <button className="px-2 py-0.5 rounded-md border border-white/[0.08] text-neutral-500 hover:text-white hover:border-violet-500/40 text-xs transition-all duration-200">
                              Script
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-white/[0.06]" />

              {/* ── WATCHLISTS ── */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-base flex items-center gap-2">
                    <span>📋</span> My Watchlists
                  </h2>
                  <button className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 transition-colors">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>

                <div className="space-y-2">
                  {WATCHLISTS.map((wl) => (
                    <div key={wl.name} className="rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-violet-500/30 p-3 transition-all duration-200 group">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg">{wl.icon}</span>
                          <div>
                            <p className="text-white text-xs font-semibold">{wl.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-neutral-600 text-xs">{wl.count} channels</span>
                              <span className="text-neutral-700">·</span>
                              <span className="text-xs">{wl.platforms.join(' ')}</span>
                            </div>
                          </div>
                        </div>
                        <button className="p-1.5 rounded-lg hover:bg-white/[0.06] text-neutral-600 hover:text-violet-400 transition-all duration-200 opacity-0 group-hover:opacity-100">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button className="w-full mt-3 py-2 rounded-xl border border-dashed border-white/[0.1] text-neutral-600 hover:text-violet-400 hover:border-violet-500/40 text-xs font-medium transition-all duration-200 flex items-center justify-center gap-1.5">
                  <Plus className="w-3 h-3" /> Add Channel
                </button>
              </div>

            </div>
          </aside>
        </div>
      </div>

      {/* ── ANALYZE DRAWER ── */}
      <AnimatePresence>
        {analyzeCard && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setAnalyzeCard(null)}
            />
            <AnalyzeDrawer card={analyzeCard} onClose={() => setAnalyzeCard(null)} />
          </>
        )}
      </AnimatePresence>

      {/* ── MOBILE FAB ── */}
      <button
        onClick={handleGenerate}
        className="xl:hidden fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-violet-600 hover:bg-violet-500 shadow-2xl shadow-violet-600/40 flex items-center justify-center transition-all duration-200 hover:scale-105"
      >
        <Zap className="w-6 h-6 text-white fill-white" />
      </button>

      {/* Ticker animation */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
